import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Edit3,
  MapPin,
  ChevronLeft,
  ChevronRight,
  Check,
  Mic,
  Image as ImageIcon,
  X,
  Sparkles,
  Video,
  Camera,
  Play,
} from 'lucide-react';
import Layout from '../components/common/Layout';
import MediaCapture from '../components/MediaCapture';
import { getMilestone, updateMilestone, deleteMilestone } from '../services/chaptersAPI';
import { mediaAPI } from '../config/mediaAPI';
import './MilestoneDetail.css';

/**
 * MilestoneEditWizard - Multi-step wizard for editing milestones
 * Step 1: Details & Date (Mockup 2)
 * Step 2: Story & Media (Mockup 3)
 * Step 3: Review & Save
 */
function MilestoneEditWizard() {
  const { id } = useParams();
  const navigate = useNavigate();

  // Wizard state
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Original data for comparison
  const [originalData, setOriginalData] = useState(null);

  // Form state - Step 1
  const [title, setTitle] = useState('');
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(null);
  const [location, setLocation] = useState('');
  const [chapter, setChapter] = useState(null);

  // Form state - Step 2
  const [text, setText] = useState('');
  const [photos, setPhotos] = useState([]);
  const [voiceFile, setVoiceFile] = useState(null);
  const [voiceDuration, setVoiceDuration] = useState(null);

  // New files to upload (unified like TellAStory)
  const [localMediaFiles, setLocalMediaFiles] = useState([]);
  const [isMediaCaptureOpen, setIsMediaCaptureOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Drag and drop state
  const [isDragOver, setIsDragOver] = useState(false);

  // Cleanup function to revoke object URLs
  const cleanupPreviewUrls = () => {
    localMediaFiles.forEach(media => {
      if (media.preview && media.preview.startsWith('blob:')) {
        URL.revokeObjectURL(media.preview);
      }
    });
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupPreviewUrls();
    };
  }, []);

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // Fetch milestone data
  useEffect(() => {
    const fetchMilestone = async () => {
      try {
        setLoading(true);
        const data = await getMilestone(id);

        // Set form state from milestone
        setTitle(data.title || '');
        setYear(data.year || new Date().getFullYear());
        setMonth(data.month || null);
        setLocation(data.location || '');
        setChapter(data.chapter || null);
        setText(data.text || '');
        // API returns photo_urls (array of {id, url, thumbnail_url}) and voice_url (string)
        setPhotos(data.photo_urls || []);
        setVoiceFile(data.voice_url || null);
        setVoiceDuration(data.voice_duration || null);

        // Store original for comparison
        setOriginalData(data);
      } catch (err) {
        console.error('Error fetching milestone:', err);
        setError('Milestone not found');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchMilestone();
    }
  }, [id]);

  // Track changes
  useEffect(() => {
    if (originalData) {
      const changed =
        title !== originalData.title ||
        year !== originalData.year ||
        month !== originalData.month ||
        location !== (originalData.location || '') ||
        text !== (originalData.text || '') ||
        localMediaFiles.length > 0;
      setHasChanges(changed);
    }
  }, [title, year, month, location, text, localMediaFiles, originalData]);

  // Handle cancel
  const handleCancel = () => {
    if (hasChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
        navigate(`/my-life/milestones/${id}`);
      }
    } else {
      navigate(`/my-life/milestones/${id}`);
    }
  };

  // Handle save
  const handleSave = async () => {
    try {
      setSaving(true);

      // Collect existing photo IDs (photos from API have 'id' field)
      const existingPhotoIds = photos
        .filter(p => p.id)
        .map(p => p.id);

      // Upload new media files and collect their IDs
      const newMediaIds = [];
      for (const media of localMediaFiles) {
        try {
          const uploadResult = await mediaAPI.uploadMedia({
            file: media.file,
            media_type: media.mediaType,
            title: title || 'Milestone media',
          });
          newMediaIds.push(uploadResult.id);
        } catch (uploadErr) {
          console.error('Error uploading media:', uploadErr);
          // Continue with other files
        }
      }

      // Combine existing and new photo IDs (for now, API expects photo_ids)
      const allPhotoIds = [...existingPhotoIds, ...newMediaIds];

      // Update milestone with all data including media
      await updateMilestone(id, {
        title,
        year,
        month,
        location: location || null,
        text: text || null,
        photo_ids: allPhotoIds.length > 0 ? allPhotoIds : undefined,
      });

      // Clear local media files
      setLocalMediaFiles([]);

      navigate(`/my-life/milestones/${id}`);
    } catch (err) {
      console.error('Error saving milestone:', err);
      alert('Failed to save milestone. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    try {
      await deleteMilestone(id);
      navigate('/my-life');
    } catch (err) {
      console.error('Error deleting milestone:', err);
      alert('Failed to delete milestone. Please try again.');
    }
  };

  // MediaCapture handlers
  const handleOpenMediaCapture = () => {
    setIsMediaCaptureOpen(true);
  };

  const handleCloseMediaCapture = () => {
    setIsMediaCaptureOpen(false);
  };

  const handleMediaCaptured = (mediaData) => {
    const localFile = {
      id: `local-${Date.now()}-${Math.random()}`,
      file: mediaData.file,
      preview: mediaData.preview,
      name: mediaData.file.name,
      size: mediaData.file.size,
      type: mediaData.file.type,
      mediaType: mediaData.type,
      duration: mediaData.duration,
      timestamp: mediaData.timestamp
    };
    setLocalMediaFiles(prev => [...prev, localFile]);
  };

  // File validation (same as TellAStory)
  const validateFile = (file, mediaType) => {
    const maxSize = 500 * 1024 * 1024; // 100MB
    if (file.size > maxSize) {
      throw new Error('File size exceeds 100MB limit');
    }
    return true;
  };

  // Handle file upload - stores files locally for preview
  const handleFileUpload = (file, mediaType) => {
    try {
      validateFile(file, mediaType);
      const previewUrl = URL.createObjectURL(file);
      const localFile = {
        id: `local-${Date.now()}-${Math.random()}`,
        file: file,
        preview: previewUrl,
        name: file.name,
        size: file.size,
        type: file.type,
        mediaType: mediaType
      };
      setLocalMediaFiles(prev => [...prev, localFile]);
      setSuccessMessage(`${file.name} added successfully!`);
    } catch (error) {
      console.error('File validation failed:', error);
      setError(`Failed to add ${file.name}: ${error.message}`);
    }
  };

  // Handle image upload (same pattern as TellAStory)
  const handleImageUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif';
    input.multiple = true;
    input.onchange = (e) => {
      const files = Array.from(e.target.files);
      files.forEach(file => {
        if (!file.type.startsWith('image/')) {
          setError(`Invalid file type: ${file.name}. Please select an image file.`);
          return;
        }
        if (file.size > 10 * 1024 * 1024) {
          setError(`Image too large: ${file.name}. Maximum size is 10MB.`);
          return;
        }
        handleFileUpload(file, 'image');
      });
    };
    input.click();
  };

  // Handle video upload (same pattern as TellAStory)
  const handleVideoUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'video/mp4,video/avi,video/quicktime,video/wmv,video/webm,video/x-matroska,.mov,.mkv';
    input.multiple = true;
    input.onchange = (e) => {
      const files = Array.from(e.target.files);
      files.forEach(file => {
        if (!file.type.startsWith('video/')) {
          setError(`Invalid file type: ${file.name}. Please select a video file.`);
          return;
        }
        if (file.size > 500 * 1024 * 1024) {
          setError(`"${file.name}" is too large (${(file.size / (1024*1024)).toFixed(0)} MB). Videos must be under 500 MB.`);
          return;
        }
        handleFileUpload(file, 'video');
      });
    };
    input.click();
  };

  // Handle audio upload (same pattern as TellAStory)
  const handleAudioUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'audio/mp3,audio/wav,audio/m4a,audio/aac,audio/ogg,audio/flac';
    input.multiple = true;
    input.onchange = (e) => {
      const files = Array.from(e.target.files);
      files.forEach(file => {
        if (!file.type.startsWith('audio/')) {
          setError(`Invalid file type: ${file.name}. Please select an audio file.`);
          return;
        }
        if (file.size > 50 * 1024 * 1024) {
          setError(`Audio file too large: ${file.name}. Maximum size is 50MB.`);
          return;
        }
        handleFileUpload(file, 'audio');
      });
    };
    input.click();
  };

  // Remove local media
  const removeLocalMedia = (fileId) => {
    setLocalMediaFiles(prev => {
      const fileToRemove = prev.find(f => f.id === fileId);
      if (fileToRemove && fileToRemove.preview) {
        URL.revokeObjectURL(fileToRemove.preview);
      }
      return prev.filter(f => f.id !== fileId);
    });
    setSuccessMessage('Media removed successfully');
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    if (imageFiles.length === 0) {
      setError('Please drop only image files.');
      return;
    }
    imageFiles.forEach(file => {
      if (file.size > 10 * 1024 * 1024) {
        setError(`Image too large: ${file.name}. Maximum size is 10MB.`);
        return;
      }
      handleFileUpload(file, 'image');
    });
    if (imageFiles.length > 0) {
      setSuccessMessage(`Added ${imageFiles.length} image${imageFiles.length > 1 ? 's' : ''} via drag & drop!`);
    }
  };

  // Format time for audio/video duration
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Get photo URL helper
  const getPhotoUrl = (photo) => {
    if (photo.preview) return photo.preview;
    // API returns full S3 URLs in the 'url' field
    if (photo.url) return photo.url;
    if (photo.thumbnail_url) return photo.thumbnail_url;
    return null;
  };

  // Word count for story text
  const wordCount = text.trim() ? text.trim().split(/\s+/).length : 0;

  // Chapter info
  const chapterTitle = chapter?.title || 'Life Chapter';
  const chapterYearRange = chapter?.start_year
    ? `${chapter.start_year} – ${chapter.end_year || 'Present'}`
    : '';

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-fb-cream">
          <div className="milestone-main">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-fb-olive mx-auto mb-4"></div>
                <p className="font-serif text-gray-600 text-lg">Loading milestone...</p>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="min-h-screen bg-fb-cream">
          <div className="milestone-main">
            <div className="bg-white rounded-xl border border-red-200 p-8 text-center">
              <span className="text-5xl mb-4 block">😔</span>
              <h3 className="font-serif font-bold text-xl text-gray-800 mb-2">
                Milestone not found
              </h3>
              <Link to="/my-life" className="text-fb-olive hover:underline font-semibold">
                Back to My Life
              </Link>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-fb-cream">
        {/* Edit Banner */}
        <div className="edit-banner">
          <div className="edit-banner-left">
            <Edit3 size={16} />
            <span>Editing: "{originalData?.title}"</span>
            {hasChanges && <span className="unsaved-dot" title="Unsaved changes" />}
          </div>
          <div className="edit-banner-actions">
            <button className="btn-cancel" onClick={handleCancel}>
              Cancel
            </button>
            <button className="btn-save" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="progress-bar-container">
          <div className="progress-steps">
            <div className={`progress-step ${step === 1 ? 'active' : step > 1 ? 'completed' : ''}`}>
              <div className="progress-dot">{step > 1 ? <Check size={14} /> : '1'}</div>
              Details & Date
            </div>
            <div className={`progress-line ${step > 1 ? 'completed' : ''}`} />
            <div className={`progress-step ${step === 2 ? 'active' : step > 2 ? 'completed' : ''}`}>
              <div className="progress-dot">{step > 2 ? <Check size={14} /> : '2'}</div>
              Story & Media
            </div>
            <div className={`progress-line ${step > 2 ? 'completed' : ''}`} />
            <div className={`progress-step ${step === 3 ? 'active' : ''}`}>
              <div className="progress-dot">3</div>
              Review
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="milestone-main">
          {/* Step 1: Details & Date */}
          {step === 1 && (
            <>
              <h1 className="section-title">Edit milestone details</h1>
              <p className="section-subtitle">Update the title, date, and location for this milestone.</p>

              {/* Title */}
              <div className="form-card">
                <div className="form-card-title">Milestone title</div>
                <div className="form-card-hint">Give this moment a name your family will remember.</div>
                <input
                  type="text"
                  className={`title-input ${title !== originalData?.title ? 'has-change' : ''}`}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={100}
                />
                <div className="input-footer">
                  {title !== originalData?.title && (
                    <div className="change-indicator">
                      <Check size={12} />
                      Modified
                    </div>
                  )}
                  <div className="char-count">{title.length} / 100</div>
                </div>
              </div>

              {/* Date */}
              <div className="form-card">
                <div className="form-card-title">When did this happen?</div>
                <div className="form-card-hint" style={{ marginBottom: '20px' }}>
                  Pick the year:
                </div>
                <div className="year-picker">
                  <button className="year-btn" onClick={() => setYear((y) => y - 1)}>
                    <ChevronLeft size={18} />
                  </button>
                  <div className="year-display">{year}</div>
                  <button className="year-btn" onClick={() => setYear((y) => y + 1)}>
                    <ChevronRight size={18} />
                  </button>
                </div>
                <div className="month-grid-label">
                  What month?{' '}
                  <span style={{ fontWeight: 400, color: '#999999', fontSize: '12px' }}>
                    (optional — just the year is fine)
                  </span>
                </div>
                <div className="month-grid">
                  {months.map((m, idx) => (
                    <button
                      key={m}
                      className={`month-btn ${month === idx + 1 ? 'selected' : ''}`}
                      onClick={() => setMonth(month === idx + 1 ? null : idx + 1)}
                    >
                      {m}
                    </button>
                  ))}
                </div>
              </div>

              {/* Location */}
              <div className="form-card">
                <div className="form-card-title">
                  Where did this happen?{' '}
                  <span style={{ fontWeight: 400, color: '#999999', fontSize: '13px' }}>(optional)</span>
                </div>
                <div className="form-card-hint">Add a place to bring this memory to life.</div>
                <div className="location-input-wrapper">
                  <div className="location-icon">
                    <MapPin size={18} />
                  </div>
                  <input
                    type="text"
                    className={`location-input ${location !== (originalData?.location || '') ? 'has-change' : ''}`}
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="e.g., Chicago, Illinois"
                  />
                </div>
              </div>

              {/* Chapter */}
              {chapter && (
                <div className="form-card">
                  <div className="form-card-title" style={{ marginBottom: '4px' }}>
                    📖 This belongs in:
                  </div>
                  <div className="form-card-hint">
                    Based on the date, this milestone fits in the chapter below.
                  </div>
                  <div className="chapter-card">
                    <div className="chapter-info">
                      <div>
                        <div className="chapter-name">Chapter {chapter.number}: "{chapterTitle}"</div>
                        <div className="chapter-range">{chapterYearRange}</div>
                      </div>
                    </div>
                    <button className="chapter-change-btn">Change chapter</button>
                  </div>
                </div>
              )}

              {/* Danger Zone */}
              <div className="danger-zone">
                <div className="danger-text">
                  <strong>Delete this milestone?</strong>
                  <br />
                  This action cannot be undone.
                </div>
                {showDeleteConfirm ? (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button className="btn-delete" onClick={handleDelete}>
                      Confirm Delete
                    </button>
                    <button
                      className="btn-secondary"
                      style={{ padding: '6px 14px', fontSize: '13px' }}
                      onClick={() => setShowDeleteConfirm(false)}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button className="btn-delete" onClick={() => setShowDeleteConfirm(true)}>
                    Delete Milestone
                  </button>
                )}
              </div>

              {/* Bottom Navigation */}
              <div className="bottom-nav">
                <button className="btn-back" onClick={() => navigate(`/my-life/milestones/${id}`)}>
                  <ChevronLeft size={16} />
                  Back to Milestone
                </button>
                <button className="btn-next" onClick={() => setStep(2)}>
                  Next: Story & Media
                  <ChevronRight size={16} />
                </button>
              </div>
              <div className="skip-link">
                <a href="#" onClick={(e) => { e.preventDefault(); handleSave(); }}>
                  or save and return to milestone view →
                </a>
              </div>
            </>
          )}

          {/* Step 2: Story & Media */}
          {step === 2 && (
            <>
              <h1 className="section-title">Share the story behind this milestone</h1>
              <p className="section-subtitle">This is optional, but your family will love to read it!</p>

              {/* Error Message */}
              {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                  {error}
                  <button
                    onClick={() => setError(null)}
                    className="ml-2 text-red-500 hover:text-red-700"
                  >
                    <X size={16} className="inline" />
                  </button>
                </div>
              )}

              {/* Success Message */}
              {successMessage && (
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
                  {successMessage}
                </div>
              )}

              {/* Media Upload */}
              <div className="form-card" style={{ backgroundColor: '#F2F2F2', padding: '24px' }}>
                <h3 className="text-xl font-semibold mb-4" style={{ color: '#333333' }}>
                  How would you like to share?
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  {/* Media Capture */}
                  <div className="text-center group">
                    <button
                      onClick={handleOpenMediaCapture}
                      className="w-full p-6 rounded-xl border-2 border-gray-300 hover:border-green-500 hover:shadow-lg bg-white transition-all duration-300 transform hover:scale-105"
                    >
                      <div className="flex flex-col items-center">
                        <div className="relative mb-3">
                          <Video className="w-10 h-10 text-green-600 group-hover:text-green-700 transition-colors" />
                          <Mic className="w-8 h-8 text-green-500 absolute -bottom-1 -right-1 group-hover:text-green-600 transition-colors" />
                        </div>
                      </div>
                      <div className="font-semibold text-lg mb-2" style={{ color: '#333333' }}>
                        Record Media
                      </div>
                      <div className="text-sm text-gray-500 mb-2">
                        Audio or Video
                      </div>
                      <div className="text-xs text-green-600 font-medium">
                        Live Recording
                      </div>
                    </button>
                  </div>

                  {/* Add Photos */}
                  <div className="text-center group">
                    <button
                      onClick={handleImageUpload}
                      className="w-full p-6 rounded-xl border-2 border-gray-300 hover:border-blue-500 hover:shadow-lg bg-white transition-all duration-300 transform hover:scale-105"
                    >
                      <div className="flex flex-col items-center">
                        <div className="relative mb-3">
                          <Camera className="w-12 h-12 text-blue-600 group-hover:text-blue-700 transition-colors" />
                          <div className="absolute -top-1 -right-1 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                            <span className="text-xs text-blue-600 font-bold">+</span>
                          </div>
                        </div>
                      </div>
                      <div className="font-semibold text-lg mb-2" style={{ color: '#333333' }}>
                        Add Photos
                      </div>
                      <div className="text-sm text-gray-500 mb-2">
                        Upload Images
                      </div>
                      <div className="text-xs text-blue-600 font-medium">
                        Multiple Formats
                      </div>
                    </button>
                  </div>

                  {/* Add Videos */}
                  <div className="text-center group">
                    <button
                      onClick={handleVideoUpload}
                      className="w-full p-6 rounded-xl border-2 border-gray-300 hover:border-purple-500 hover:shadow-lg bg-white transition-all duration-300 transform hover:scale-105"
                    >
                      <div className="flex flex-col items-center">
                        <div className="relative mb-3">
                          <Video className="w-12 h-12 text-purple-600 group-hover:text-purple-700 transition-colors" />
                          <div className="absolute -top-1 -right-1 w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center">
                            <span className="text-xs text-purple-600 font-bold">+</span>
                          </div>
                        </div>
                      </div>
                      <div className="font-semibold text-lg mb-2" style={{ color: '#333333' }}>
                        Add Videos
                      </div>
                      <div className="text-sm text-gray-500 mb-2">
                        Upload Files
                      </div>
                      <div className="text-xs text-purple-600 font-medium">
                        MP4, AVI, MOV
                      </div>
                    </button>
                  </div>

                  {/* Add Audio Files */}
                  <div className="text-center group">
                    <button
                      onClick={handleAudioUpload}
                      className="w-full p-6 rounded-xl border-2 border-gray-300 hover:border-orange-500 hover:shadow-lg bg-white transition-all duration-300 transform hover:scale-105"
                    >
                      <div className="flex flex-col items-center">
                        <div className="relative mb-3">
                          <Mic className="w-12 h-12 text-orange-600 group-hover:text-orange-700 transition-colors" />
                          <div className="absolute -top-1 -right-1 w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center">
                            <span className="text-xs text-orange-600 font-bold">+</span>
                          </div>
                        </div>
                      </div>
                      <div className="font-semibold text-lg mb-2" style={{ color: '#333333' }}>
                        Add Audio
                      </div>
                      <div className="text-sm text-gray-500 mb-2">
                        Upload Files
                      </div>
                      <div className="text-xs text-orange-600 font-medium">
                        MP3, WAV, M4A
                      </div>
                    </button>
                  </div>
                </div>

                {/* Media Files Display (same as TellAStory) */}
                {(photos.length > 0 || localMediaFiles.length > 0 || voiceFile) && (
                  <div className="mb-6">
                    <div className="text-sm font-medium mb-2" style={{ color: '#333333' }}>
                      Media Files ({photos.length + localMediaFiles.length + (voiceFile ? 1 : 0)}):
                    </div>
                    <div className="grid grid-cols-3 md:grid-cols-4 gap-4">
                      {/* Existing photos from API */}
                      {photos.map((photo, idx) => (
                        <div key={`existing-${idx}`} className="relative group">
                          <div className="relative">
                            {getPhotoUrl(photo) ? (
                              <img
                                src={getPhotoUrl(photo)}
                                alt={`Photo ${idx + 1}`}
                                className="w-full h-24 object-cover rounded-lg border border-gray-200"
                              />
                            ) : (
                              <div className="w-full h-24 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center">
                                <ImageIcon className="w-8 h-8 text-gray-400" />
                              </div>
                            )}
                            <div className="absolute top-2 left-2 px-2 py-1 bg-black bg-opacity-70 text-white text-xs rounded">
                              IMAGE
                            </div>
                          </div>
                        </div>
                      ))}
                      {/* New local media files */}
                      {localMediaFiles.map((media) => (
                        <div key={media.id} className="relative group">
                          <div className="relative">
                            {media.mediaType === 'image' ? (
                              <img
                                src={media.preview}
                                alt={media.name}
                                className="w-full h-24 object-cover rounded-lg border border-gray-200"
                              />
                            ) : media.mediaType === 'video' ? (
                              <div className="w-full h-24 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center relative">
                                <video
                                  src={media.preview}
                                  className="w-full h-full object-cover rounded-lg"
                                  muted
                                  preload="metadata"
                                />
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <Play className="w-8 h-8 text-white drop-shadow-lg" />
                                </div>
                                {media.duration && (
                                  <div className="absolute bottom-2 right-2 px-2 py-1 bg-black bg-opacity-70 text-white text-xs rounded">
                                    {formatTime(media.duration)}
                                  </div>
                                )}
                              </div>
                            ) : media.mediaType === 'audio' ? (
                              <div className="w-full h-24 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center relative">
                                <Mic className="w-8 h-8 text-gray-400" />
                                {media.duration && (
                                  <div className="absolute bottom-2 right-2 px-2 py-1 bg-black bg-opacity-70 text-white text-xs rounded">
                                    {formatTime(media.duration)}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="w-full h-24 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center">
                                <Mic className="w-8 h-8 text-gray-400" />
                              </div>
                            )}
                            {/* Media type indicator */}
                            <div className="absolute top-2 left-2 px-2 py-1 bg-black bg-opacity-70 text-white text-xs rounded">
                              {media.mediaType.toUpperCase()}
                            </div>
                          </div>
                          <div className="text-xs text-gray-500 mt-1 truncate">{media.name}</div>
                          <div className="text-xs text-gray-400">{formatFileSize(media.size)}</div>
                          <button
                            onClick={() => removeLocalMedia(media.id)}
                            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      {/* Existing voice file */}
                      {voiceFile && (
                        <div className="relative group">
                          <div className="relative">
                            <div className="w-full h-24 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center">
                              <Mic className="w-8 h-8 text-orange-500" />
                            </div>
                            <div className="absolute top-2 left-2 px-2 py-1 bg-black bg-opacity-70 text-white text-xs rounded">
                              AUDIO
                            </div>
                            {voiceDuration && (
                              <div className="absolute bottom-2 right-2 px-2 py-1 bg-black bg-opacity-70 text-white text-xs rounded">
                                {formatTime(voiceDuration)}
                              </div>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">Voice Recording</div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Drag & Drop Area for Photos */}
                <div
                  className={`mt-6 p-6 border-2 border-dashed rounded-xl transition-all duration-200 ${
                    isDragOver
                      ? 'border-blue-400 bg-blue-50 shadow-lg'
                      : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <div className="text-center">
                    <Camera className={`w-12 h-12 mx-auto mb-3 transition-colors ${
                      isDragOver ? 'text-blue-500' : 'text-gray-400'
                    }`} />
                    <h4 className={`text-lg font-medium mb-2 transition-colors ${
                      isDragOver ? 'text-blue-700' : 'text-gray-700'
                    }`}>
                      {isDragOver ? 'Drop Photos Here!' : 'Drag & Drop Photos Here'}
                    </h4>
                    <p className="text-sm text-gray-500 mb-3">
                      Or click the "Add Photos" button above to browse files
                    </p>
                    <div className="text-xs text-gray-400">
                      Supports: JPEG, PNG, GIF, WebP, HEIC
                    </div>
                  </div>
                </div>
              </div>

              {/* Story Text */}
              <div className="form-card">
                <div className="form-card-title">Or write your story:</div>
                <div className="form-card-hint">What happened? How did it feel? Who was there with you?</div>
                <textarea
                  className="story-textarea"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Tell your story..."
                />
                <div className="textarea-footer">
                  <div className="word-count">{wordCount} words</div>
                  <button className="ai-assist-btn">
                    <Sparkles size={13} />
                    AI: Help me write
                  </button>
                </div>
                <div className="helper-text">
                  <Check size={12} style={{ display: 'inline', verticalAlign: '-1px' }} />
                  {' '}You can always add more details later
                </div>
              </div>

              {/* Voice Recording */}
              <div className="form-card">
                <div className="form-card-title">Voice recording</div>
                <div className="form-card-hint">
                  Record yourself telling this story in your own voice — your family will treasure it.
                </div>
                <div className="voice-record-section">
                  <button className="voice-btn" title="Record new audio" onClick={handleOpenMediaCapture}>
                    <Mic size={20} />
                  </button>
                  <div className="voice-info">
                    <div className="voice-title">
                      {voiceFile || localMediaFiles.some(m => m.mediaType === 'audio') ? 'Replace or add a recording' : 'Record your voice'}
                    </div>
                    {voiceFile && (
                      <div className="voice-existing">
                        <span className="voice-existing-dot" />
                        Existing recording: {voiceDuration ? `${Math.floor(voiceDuration / 60)}:${String(voiceDuration % 60).padStart(2, '0')}` : 'Audio'}
                      </div>
                    )}
                    {!voiceFile && !localMediaFiles.some(m => m.mediaType === 'audio') && (
                      <div className="voice-hint">Tap to start recording</div>
                    )}
                    {localMediaFiles.some(m => m.mediaType === 'audio') && (
                      <div className="voice-existing">
                        <span className="voice-existing-dot" />
                        New recording added
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Bottom Navigation */}
              <div className="bottom-nav">
                <button className="btn-back" onClick={() => setStep(1)}>
                  <ChevronLeft size={16} />
                  Back: Details
                </button>
                <button className="btn-next" onClick={handleSave} disabled={saving}>
                  <Check size={16} />
                  {saving ? 'Saving...' : 'Save Milestone'}
                </button>
              </div>
              <div className="skip-link">
                <a href="#" onClick={(e) => { e.preventDefault(); handleSave(); }}>
                  Skip for now — I'll add the story later
                </a>
              </div>
            </>
          )}
        </div>
      </div>

      {/* MediaCapture Modal */}
      <MediaCapture
        isOpen={isMediaCaptureOpen}
        onClose={handleCloseMediaCapture}
        onMediaCaptured={handleMediaCaptured}
      />
    </Layout>
  );
}

export default MilestoneEditWizard;
